<template>
  <ListPageShell>
    <template v-if="!embedded" #header>
      <div class="list-page-panel is-header-minimal px-4 md:px-6 py-3">
        <div class="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
          <div class="max-w-3xl">
            <h1 class="list-page-title">提醒定义</h1>
            <p class="mt-2 text-sm leading-6 text-slate-600">只展示由你创建的提醒定义。可复用现有账单/方案提醒能力，为多人配置自定义提醒、重复规则和跳转路径。</p>
          </div>
        </div>
      </div>
    </template>

    <template #filters>
      <PageFilterBar :show-refresh="true" :refresh-loading="loading" @refresh="fetchList">
        <div class="relative filter-bar-field">
          <svg class="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            v-model="filters.keyword"
            type="text"
            placeholder="搜索标题或提醒内容"
            class="form-search-input form-control-sm w-full"
            @keyup.enter="handleSearch"
          />
        </div>

        <el-select v-model="filters.status" clearable class="filter-bar-field-sm" placeholder="全部状态">
          <el-option label="启用中" value="ENABLED" />
          <el-option label="已停用" value="DISABLED" />
        </el-select>

        <template v-if="!embedded" #actions>
          <router-link
            to="/reminders/my-tasks"
            class="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition-colors hover:border-slate-300 hover:text-slate-900"
          >
            <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
            我的任务
          </router-link>
        </template>

        <template #searchActions>
          <button type="button" class="btn-primary inline-flex items-center gap-2" @click="handleSearch">
            <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            搜索
          </button>
          <button type="button" class="btn-secondary" @click="handleReset">重置</button>
          <button type="button" class="btn-primary inline-flex items-center gap-2" @click="openCreate">
            <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
            </svg>
            新建提醒
          </button>
        </template>
      </PageFilterBar>
    </template>

    <div :class="contentCardClass">
      <div class="data-table-shell border-none shadow-none">
        <div class="data-table-scroll">
          <table class="data-table">
            <thead class="data-table-head">
              <tr>
                <th class="data-table-th text-left">提醒标题</th>
                <th class="data-table-th text-left">首次提醒</th>
                <th class="data-table-th text-left">重复规则</th>
                <th class="data-table-th text-left">截止时间</th>
                <th class="data-table-th text-left">接收人数</th>
                <th class="data-table-th text-left">状态</th>
                <th class="data-table-th text-left">创建时间</th>
                <th class="data-table-th is-center">操作</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in list" :key="item.id" class="data-table-row">
                <td class="data-table-td min-w-[280px]">
                  <div class="flex items-start gap-3">
                    <div class="mt-1 flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-blue-50 text-blue-600">
                      <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V4a2 2 0 10-4 0v1.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                      </svg>
                    </div>
                    <div class="min-w-0">
                      <p class="truncate text-sm font-semibold text-slate-900">{{ item.title }}</p>
                      <p class="mt-1 text-xs text-slate-500">定义 ID: {{ item.id }}</p>
                    </div>
                  </div>
                </td>
                <td class="data-table-td whitespace-nowrap text-sm text-slate-600">{{ formatDateTime(item.firstSendTime) || '-' }}</td>
                <td class="data-table-td whitespace-nowrap text-sm text-slate-600">{{ formatRepeat(item) }}</td>
                <td class="data-table-td whitespace-nowrap text-sm text-slate-600">{{ formatDateTime(item.deadlineTime) || '未设置' }}</td>
                <td class="data-table-td whitespace-nowrap">
                  <button
                    type="button"
                    class="inline-flex items-center gap-1.5 rounded-full bg-orange-50 px-3 py-1 text-xs font-medium text-orange-700 transition-colors hover:bg-orange-100 hover:text-orange-800"
                    title="查看接收人处理状态"
                    @click="openReceiverStatusDialog(item)"
                  >
                    <span>{{ item.receiverCount || 0 }} 人</span>
                    <svg class="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </td>
                <td class="data-table-td whitespace-nowrap">
                  <span :class="['status-badge', item.status === 'ENABLED' ? 'is-success' : 'is-neutral']">{{ item.statusDesc || statusText(item.status) }}</span>
                </td>
                <td class="data-table-td whitespace-nowrap text-sm text-slate-600">{{ formatDateTime(item.createTime) || '-' }}</td>
                <td class="data-table-td is-center whitespace-nowrap">
                  <div class="flex items-center justify-center gap-1">
                    <button class="table-action-button is-primary" title="编辑" @click="handleEdit(item.id)">
                      <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5M18.586 2.586a2 2 0 112.828 2.828L12 14.828l-4 1 1-4 9.586-9.242z" />
                      </svg>
                    </button>
                    <button
                      v-if="item.status === 'ENABLED'"
                      class="table-action-button"
                      title="停用"
                      @click="toggleStatus(item, false)"
                    >
                      <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 9v6m4-6v6M6 6h12l-1 14H7L6 6zm3-3h6l1 3H8l1-3z" />
                      </svg>
                    </button>
                    <button
                      v-else
                      class="table-action-button is-success"
                      title="启用"
                      @click="toggleStatus(item, true)"
                    >
                      <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.868v4.264a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 12A9 9 0 113 12a9 9 0 0118 0z" />
                      </svg>
                    </button>
                    <button class="table-action-button is-danger" title="删除" @click="handleDelete(item)">
                      <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div v-if="!loading && !list.length" class="flex flex-col items-center justify-center py-20">
        <div class="flex h-20 w-20 items-center justify-center rounded-full bg-slate-100 text-slate-400">
          <svg class="h-10 w-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V4a2 2 0 10-4 0v1.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
          </svg>
        </div>
        <p class="mt-5 text-sm text-slate-500">你还没有创建提醒定义</p>
        <button type="button" class="btn-primary mt-5" @click="openCreate">立即创建</button>
      </div>

      <div v-if="loading" class="flex items-center justify-center py-14">
        <svg class="h-8 w-8 animate-spin text-blue-500" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
        </svg>
      </div>

      <AppPagination
        class="pagination-bridge"
        :pagination="pagination"
        :page-sizes="[10, 20, 50]"
        :show-page-count="true"
        @page-change="handlePageChange"
        @size-change="handlePageSizeChange"
      />
    </div>

    <ReminderDefinitionFormModal
      v-model:visible="modalVisible"
      :mode="modalMode"
      :detail="editingDetail"
      :saving="submitting"
      @submit="submitForm"
    />

    <div v-if="receiverDialogVisible" class="fixed inset-0 z-[60] overflow-y-auto" @click.self="closeReceiverStatusDialog">
      <div class="fixed inset-0 bg-slate-900/50 backdrop-blur-sm" @click="closeReceiverStatusDialog"></div>
      <div class="relative flex min-h-screen items-center justify-center p-4 md:p-6">
        <div class="relative flex max-h-[min(760px,calc(100vh-2rem))] w-full max-w-3xl flex-col overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-[0_28px_80px_rgba(15,23,42,0.22)]">
          <div class="flex items-start justify-between gap-4 border-b border-slate-200 bg-slate-50 px-5 py-4 md:px-6">
            <div class="min-w-0">
              <h2 class="truncate text-lg font-semibold text-slate-900">接收人处理状态</h2>
              <p class="mt-1 truncate text-sm text-slate-500">{{ selectedReceiverDefinition?.title || '未命名提醒' }}</p>
            </div>
            <button
              type="button"
              class="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-slate-200 text-slate-500 transition-colors hover:border-slate-300 hover:text-slate-900"
              title="关闭"
              @click="closeReceiverStatusDialog"
            >
              <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div class="border-b border-slate-100 px-5 py-4 md:px-6">
            <div class="grid grid-cols-2 gap-3 md:grid-cols-5">
              <div class="rounded-xl border border-slate-100 bg-slate-50 px-3 py-3">
                <p class="text-xs text-slate-500">接收人数</p>
                <p class="mt-1 text-lg font-semibold text-slate-900">{{ receiverStatusStats.total }}</p>
              </div>
              <div class="rounded-xl border border-amber-100 bg-amber-50 px-3 py-3">
                <p class="text-xs text-amber-600">未处理</p>
                <p class="mt-1 text-lg font-semibold text-amber-700">{{ receiverStatusStats.unhandled }}</p>
              </div>
              <div class="rounded-xl border border-slate-100 bg-slate-50 px-3 py-3">
                <p class="text-xs text-slate-600">待提醒</p>
                <p class="mt-1 text-lg font-semibold text-slate-700">{{ receiverStatusStats.pendingRemind }}</p>
              </div>
              <div class="rounded-xl border border-blue-100 bg-blue-50 px-3 py-3">
                <p class="text-xs text-blue-600">已转待办</p>
                <p class="mt-1 text-lg font-semibold text-blue-700">{{ receiverStatusStats.converted }}</p>
              </div>
              <div class="rounded-xl border border-violet-100 bg-violet-50 px-3 py-3">
                <p class="text-xs text-violet-600">已延期</p>
                <p class="mt-1 text-lg font-semibold text-violet-700">{{ receiverStatusStats.postponed }}</p>
              </div>
              <div class="rounded-xl border border-emerald-100 bg-emerald-50 px-3 py-3">
                <p class="text-xs text-emerald-600">已处理</p>
                <p class="mt-1 text-lg font-semibold text-emerald-700">{{ receiverStatusStats.handled }}</p>
              </div>
            </div>
          </div>

          <div class="flex-1 overflow-y-auto px-5 py-4 md:px-6">
            <div v-if="receiverDialogLoading" class="flex items-center justify-center py-12">
              <svg class="h-7 w-7 animate-spin text-blue-500" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
              </svg>
            </div>

            <div v-else-if="selectedReceivers.length" class="overflow-hidden rounded-xl border border-slate-200">
              <table class="min-w-full divide-y divide-slate-200">
                <thead class="bg-slate-50">
                  <tr>
                    <th class="px-4 py-3 text-left text-xs font-semibold text-slate-500">接收人</th>
                    <th class="px-4 py-3 text-left text-xs font-semibold text-slate-500">处理状态</th>
                    <th class="px-4 py-3 text-left text-xs font-semibold text-slate-500">处理时间</th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-slate-100 bg-white">
                  <tr v-for="receiver in selectedReceivers" :key="receiver.userId" class="hover:bg-slate-50/70">
                    <td class="px-4 py-3">
                      <div class="flex items-center gap-3">
                        <div class="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-slate-100 text-sm font-semibold text-slate-600">
                          {{ receiverAvatarText(receiver) }}
                        </div>
                        <div class="min-w-0">
                          <p class="truncate text-sm font-medium text-slate-900">{{ receiver.userName || '未命名用户' }}</p>
                          <p class="mt-0.5 text-xs text-slate-400">ID: {{ receiver.userId || '-' }}</p>
                        </div>
                      </div>
                    </td>
                    <td class="px-4 py-3">
                      <div class="flex flex-col items-start gap-1">
                        <span :class="['status-badge', receiverResponseStatusClass(receiver)]">
                          {{ receiverResponseStatusText(receiver) }}
                        </span>
                        <span v-if="receiverPostponedTimeText(receiver)" class="text-xs text-slate-500">
                          延期至 {{ receiverPostponedTimeText(receiver) }}
                        </span>
                      </div>
                    </td>
                    <td class="px-4 py-3 whitespace-nowrap text-sm text-slate-600">
                      {{ formatDateTime(receiver.handledTime) || '-' }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div v-else class="flex flex-col items-center justify-center rounded-xl border border-dashed border-slate-200 py-12">
              <div class="flex h-14 w-14 items-center justify-center rounded-full bg-slate-100 text-slate-400">
                <svg class="h-7 w-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.8" d="M17 20h5v-2a4 4 0 00-4-4h-1M9 20H4v-2a4 4 0 014-4h1m4-4a4 4 0 100-8 4 4 0 000 8zm6 2a3 3 0 100-6" />
                </svg>
              </div>
              <p class="mt-4 text-sm text-slate-500">暂无接收人数据</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </ListPageShell>
</template>

<script setup>
import { computed, getCurrentInstance, onMounted, reactive, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { ElMessage, ElMessageBox } from 'element-plus'
import AppPagination from '@/components/AppPagination.vue'
import ListPageShell from '@/components/ListPageShell.vue'
import PageFilterBar from '@/components/PageFilterBar.vue'
import ReminderDefinitionFormModal from '@/views/reminder/components/ReminderDefinitionFormModal.vue'
import {
  createReminderDefinition,
  deleteReminderDefinition,
  disableReminderDefinition,
  enableReminderDefinition,
  getReminderDefinitionDetail,
  getReminderDefinitionPage,
  updateReminderDefinition
} from '@/api/reminder'

const props = defineProps({
  embedded: {
    type: Boolean,
    default: false
  }
})

const route = useRoute()
const router = useRouter()
const { proxy } = getCurrentInstance()

const loading = ref(false)
const submitting = ref(false)
const list = ref([])
const editingDetail = ref(null)
const modalVisible = ref(false)
const modalMode = ref('create')
const receiverDialogVisible = ref(false)
const receiverDialogLoading = ref(false)
const selectedReceiverDefinition = ref(null)

const filters = reactive({
  keyword: '',
  status: ''
})

const pagination = ref({
  current: 1,
  size: 10,
  total: 0,
  pages: 0
})

const embedded = computed(() => props.embedded)
const contentCardClass = computed(() => (
  embedded.value
    ? 'rounded-[24px] border border-slate-200 bg-slate-50/65 p-4 shadow-none'
    : 'rounded-[28px] border border-slate-200 bg-white p-4 shadow-[0_18px_50px_rgba(15,23,42,0.06)]'
))

const selectedReceivers = computed(() => selectedReceiverDefinition.value?.receivers || [])
const receiverStatusStats = computed(() => {
  const receivers = selectedReceivers.value
  return {
    total: receivers.length,
    pendingRemind: receivers.filter(item => resolveReceiverDisplayStatus(item) === 'PENDING_REMIND').length,
    unhandled: receivers.filter(item => resolveReceiverDisplayStatus(item) === 'UNHANDLED').length,
    converted: receivers.filter(item => resolveReceiverDisplayStatus(item) === 'CONVERTED_TO_TODO').length,
    postponed: receivers.filter(item => resolveReceiverDisplayStatus(item) === 'POSTPONED').length,
    handled: receivers.filter(item => resolveReceiverDisplayStatus(item) === 'HANDLED').length
  }
})

// const summary = computed(() => ({
//   total: pagination.value.total || 0,
//   enabled: list.value.filter(item => item.status === 'ENABLED').length,
//   receivers: list.value.reduce((sum, item) => sum + Number(item.receiverCount || 0), 0)
// }))

watch(
  () => route.query.edit,
  async (editId) => {
    if (embedded.value) return
    if (!editId) return
    await openEditById(editId)
  },
  { immediate: true }
)

watch(modalVisible, (visible) => {
  if (embedded.value) return
  if (!visible && route.query.edit) {
    const nextQuery = { ...route.query }
    delete nextQuery.edit
    router.replace({ query: nextQuery })
  }
})

function formatDateTime(value) {
  if (!value) return ''
  if (typeof proxy?.$formatDateTime === 'function') {
    return proxy.$formatDateTime(value)
  }
  return String(value).replace('T', ' ')
}

function statusText(status) {
  return status === 'ENABLED' ? '启用中' : '已停用'
}

function normalizeReceiverResponseStatus(status) {
  return String(status || 'UNHANDLED').toUpperCase()
}

function isBeforeFirstSendTime() {
  const firstSendTime = selectedReceiverDefinition.value?.firstSendTime
  if (!firstSendTime) return false
  const date = new Date(String(firstSendTime).replace(' ', 'T'))
  return !Number.isNaN(date.getTime()) && date.getTime() > Date.now()
}

function resolveReceiverDisplayStatus(receiver) {
  const normalized = normalizeReceiverResponseStatus(receiver?.responseStatus)
  if (normalized === 'UNHANDLED' && isBeforeFirstSendTime()) {
    return 'PENDING_REMIND'
  }
  return normalized
}

function receiverResponseStatusText(receiver) {
  const normalized = resolveReceiverDisplayStatus(receiver)
  if (normalized === 'PENDING_REMIND') return '待提醒'
  if (normalized === 'HANDLED') return '已处理'
  if (normalized === 'CONVERTED_TO_TODO') return '已转待办'
  if (normalized === 'POSTPONED') return '已延期'
  if (normalized === 'TIMEOUT') return '已超时'
  return receiver?.responseStatusDesc || '未处理'
}

function receiverResponseStatusClass(receiver) {
  const normalized = resolveReceiverDisplayStatus(receiver)
  if (normalized === 'HANDLED') return 'is-success'
  if (normalized === 'CONVERTED_TO_TODO') return 'is-info'
  if (normalized === 'POSTPONED') return 'is-purple'
  if (normalized === 'TIMEOUT') return 'is-danger'
  if (normalized === 'PENDING_REMIND') return 'is-neutral'
  return 'is-warning'
}

function receiverPostponedTimeText(receiver) {
  if (resolveReceiverDisplayStatus(receiver) !== 'POSTPONED') {
    return ''
  }
  return formatDateTime(receiver?.postponedTime) || ''
}

function receiverAvatarText(receiver) {
  const name = receiver?.userName || String(receiver?.userId || '')
  return name ? name.slice(0, 1).toUpperCase() : '-'
}

function formatRepeat(item) {
  if (!item.repeatEnabled) return '单次提醒'
  return '重复提醒'
}

async function fetchList() {
  loading.value = true
  try {
    const result = await getReminderDefinitionPage({
      pageNum: pagination.value.current,
      pageSize: pagination.value.size,
      keyword: filters.keyword || undefined,
      status: filters.status || undefined
    })
    list.value = result?.records || []
    pagination.value = {
      current: result?.current || pagination.value.current,
      size: result?.size || pagination.value.size,
      total: result?.total || 0,
      pages: result?.pages || 0
    }
  } catch (error) {
    ElMessage.error('获取提醒定义失败')
    console.error('获取提醒定义失败', error)
  } finally {
    loading.value = false
  }
}

function handleSearch() {
  pagination.value.current = 1
  fetchList()
}

function handleReset() {
  filters.keyword = ''
  filters.status = ''
  pagination.value.current = 1
  fetchList()
}

function handlePageChange(page) {
  pagination.value.current = page
  fetchList()
}

function handlePageSizeChange(size) {
  pagination.value.size = size
  pagination.value.current = 1
  fetchList()
}

function openCreate() {
  modalMode.value = 'create'
  editingDetail.value = null
  modalVisible.value = true
}

async function handleEdit(id) {
  if (embedded.value) {
    await openEditById(id)
    return
  }
  await router.replace({
    query: {
      ...route.query,
      edit: id
    }
  })
}

async function openEditById(id) {
  try {
    const detail = await getReminderDefinitionDetail(id)
    editingDetail.value = detail
    modalMode.value = 'edit'
    modalVisible.value = true
  } catch (error) {
    ElMessage.error('获取提醒详情失败')
    console.error('获取提醒详情失败', error)
  }
}

async function openReceiverStatusDialog(item) {
  selectedReceiverDefinition.value = {
    ...item,
    receivers: item?.receivers || []
  }
  receiverDialogVisible.value = true
  if (selectedReceiverDefinition.value.receivers.length || !item?.id) {
    return
  }
  receiverDialogLoading.value = true
  try {
    const detail = await getReminderDefinitionDetail(item.id)
    selectedReceiverDefinition.value = {
      ...item,
      ...detail,
      receivers: detail?.receivers || []
    }
  } catch (error) {
    ElMessage.error('获取接收人状态失败')
    console.error('获取接收人状态失败', error)
  } finally {
    receiverDialogLoading.value = false
  }
}

function closeReceiverStatusDialog() {
  receiverDialogVisible.value = false
  receiverDialogLoading.value = false
  selectedReceiverDefinition.value = null
}

async function toggleStatus(item, enabled) {
  const actionText = enabled ? '启用' : '停用'
  try {
    await ElMessageBox.confirm(`确定要${actionText}提醒“${item.title}”吗？`, `${actionText}确认`, {
      confirmButtonText: actionText,
      cancelButtonText: '取消',
      type: enabled ? 'success' : 'warning'
    })
    if (enabled) {
      await enableReminderDefinition(item.id)
    } else {
      await disableReminderDefinition(item.id)
    }
    ElMessage.success(`${actionText}成功`)
    await fetchList()
  } catch (error) {
    if (error !== 'cancel') {
      ElMessage.error(`${actionText}失败`)
      console.error(`${actionText}失败`, error)
    }
  }
}

async function handleDelete(item) {
  try {
    await ElMessageBox.confirm(`确定删除提醒“${item.title}”吗？删除后不可恢复。`, '删除确认', {
      confirmButtonText: '删除',
      cancelButtonText: '取消',
      type: 'warning'
    })
    await deleteReminderDefinition(item.id)
    ElMessage.success('删除成功')
    if (list.value.length === 1 && pagination.value.current > 1) {
      pagination.value.current -= 1
    }
    await fetchList()
  } catch (error) {
    if (error !== 'cancel') {
      ElMessage.error('删除失败')
      console.error('删除失败', error)
    }
  }
}

async function submitForm(payload) {
  submitting.value = true
  try {
    if (modalMode.value === 'edit' && editingDetail.value?.id) {
      await updateReminderDefinition(editingDetail.value.id, payload)
      ElMessage.success('提醒定义已更新')
    } else {
      await createReminderDefinition(payload)
      ElMessage.success('提醒定义已创建')
    }
    modalVisible.value = false
    await fetchList()
  } catch (error) {
    ElMessage.error(error?.message || '保存提醒定义失败')
    console.error('保存提醒定义失败', error)
  } finally {
    submitting.value = false
  }
}

onMounted(() => {
  fetchList()
})
</script>

<style scoped>
:deep(.status-badge.is-success) {
  background: #dcfce7;
  color: #15803d;
}

:deep(.status-badge.is-neutral) {
  background: #e2e8f0;
  color: #475569;
}

:deep(.status-badge.is-info) {
  background: #dbeafe;
  color: #1d4ed8;
}

:deep(.status-badge.is-warning) {
  background: #fef3c7;
  color: #b45309;
}

:deep(.status-badge.is-purple) {
  background: #ede9fe;
  color: #6d28d9;
}

:deep(.status-badge.is-danger) {
  background: #ffe4e6;
  color: #be123c;
}
</style>
